// chat/actions.ts — the chat control EXECUTOR (cloned + adapted from Oriora Expense's
// src/chat/actions.ts). The client router (chat/brain.ts) maps ONE user message → ONE
// ChatAction; this file EXECUTES that action against THIS app's on-device store
// (src/store.ts). Every mutating call is LOCAL SQLite over Tauri IPC — no network, no billing.
//
// The ONE AI-billed step ("make cards from …notes…") runs the app's EXISTING live endpoint via
// brain.generateCards — no new/edited server route from the chat.
//
// The action set is DERIVED FROM THIS STORE (not copied from Expense): the store exposes
// createDeck / createCard(s) / updateCard / deleteDeck / deleteCard / listCards, so the chat can
// add deck · add card · search cards · "what's due" · delete-with-confirm · generate-from-notes.
//
// HITL discipline (mirrors Expense): destructive actions (delete_deck / delete_card) are NEVER
// executed from a chat turn on their own — the Chat UI holds them and requires an explicit
// Confirm tap. This executor just performs the delete once the UI hands it back.

import { store, normalizeTag, type Card, type Deck, type CardPatch } from "../store";
import { exportBackup, importBackup } from "../backup";
import { invoke } from "../tauri-web";
import { saveCopyNow, importFromFile } from "../localFolder";
const LOCAL_SLUG = "flashcards";
import { cloudBackupSave, cloudBackupLoad, cloudBackupStatus, cloudStoreSave } from "../brain";
import { addInstruction } from "./brain";
import { isDue } from "../fsrs";
import { generateCards, BrainError } from "../brain";
import { libraryAdd, libraryList, libraryRemove, libraryMove, libraryMerge } from "../library";

// A compact card row rendered inline in a chat bubble (search results).
export interface FilterRow {
  id: string;
  front: string;
  back: string;
  tag: string;
  due: boolean;
}

// A purely LOCAL filter over the user's own cards (no server route — search is
// computed on-device from listCards).
export interface CardFilter {
  /** Free-text contains over front / back / tag. */
  text?: string;
  tag?: string;
  deckName?: string;
  dueOnly?: boolean;
}

export type ChatAction =
  | { kind: "none"; message?: string }
  | { kind: "add_deck"; name: string; description?: string }
  | { kind: "add_card"; deckName?: string; front: string; back: string; tag?: string }
  | { kind: "delete_deck"; id: string; label?: string }  // UI confirms before this reaches executeAction
  | { kind: "delete_card"; id: string; label?: string }  // UI confirms before this reaches executeAction
  // EDIT, not replace: only the fields the customer actually named are sent, so "fix the back of
  // that card" cannot silently blank its front or drop its tag.
  | { kind: "update_card"; id: string; front?: string; back?: string; tag?: string; label?: string }
  | { kind: "update_deck"; id: string; name: string; label?: string }
  | { kind: "filter"; filter: CardFilter; label?: string }
  | { kind: "due_summary" }
  | { kind: "generate"; notes: string; deckName?: string } // the ONE billed step
  | { kind: "backup_save" }
  | { kind: "backup_status" }
  | { kind: "backup_restore"; provider?: "github" | "gdrive" } // UI confirms before executeAction (replaces all data)
  | { kind: "save_local_copy" }
  | { kind: "import_file" }
  | { kind: "save_to_cloud"; contentKind: "conversation" | "note"; note?: string; destination?: "github" | "gdrive" }
  | { kind: "remember"; instruction: string }
  // ── ARTIFACT TOOLS — the customer's library (fleet-wide) ────────────────────
  | { kind: "library_save"; title: string; itemKind: "lesson" | "plan" | "note"; content: string; folder?: string }
  | { kind: "library_list"; folder?: string }
  | { kind: "library_remove"; id: string; label?: string }   // UI confirms first
  | { kind: "library_move"; id: string; folder: string; label?: string }
  | { kind: "library_merge"; ids: string[]; title: string; label?: string };  // UI confirms first

export interface ActionResult {
  ok: boolean;
  message: string;   // shown back in the chat
  changed?: boolean; // the collection changed → the main list should refresh
  rows?: FilterRow[]; // search matches to render inline
}

const norm = (s: string) => s.trim().toLowerCase();

async function resolveDeck(name?: string): Promise<Deck | null> {
  const decks = await store.listDecks();
  if (!name) return decks[0] ?? null;
  const q = norm(name);
  return decks.find((d) => norm(d.name) === q) ?? decks.find((d) => norm(d.name).includes(q)) ?? null;
}

/** Apply a CardFilter locally — every condition ANDs; empty filter matches all. */
export function applyCardFilter(cards: Card[], f: CardFilter, deckIds?: Set<string>): Card[] {
  const now = new Date();
  return cards.filter((c) => {
    if (deckIds && !deckIds.has(c.deck_id)) return false;
    if (f.tag && !c.tag.includes(norm(f.tag))) return false;
    if (f.dueOnly && !isDue(c, now)) return false;
    if (f.text) {
      const t = norm(f.text);
      const hit = c.front.toLowerCase().includes(t) || c.back.toLowerCase().includes(t) || c.tag.includes(t);
      if (!hit) return false;
    }
    return true;
  });
}

export async function executeAction(a: ChatAction, ctx?: { transcript?: string }): Promise<ActionResult> {
  try {
    switch (a.kind) {
      case "none":
        return { ok: true, message: a.message ?? "" };

      case "add_deck": {
        const d = await store.createDeck({ name: a.name, description: a.description ?? "" });
        return { ok: true, message: `Created deck "${d.name}".`, changed: true };
      }

      case "add_card": {
        const deck = await resolveDeck(a.deckName);
        if (!deck) return { ok: false, message: a.deckName ? `I couldn't find a deck matching "${a.deckName}".` : 'No decks yet — say "add deck Biology" first.' };
        await store.createCard({ deck_id: deck.id, front: a.front, back: a.back, tag: a.tag, source: "typed" });
        return { ok: true, message: `Added a card to "${deck.name}": ${a.front}`, changed: true };
      }

      case "delete_deck":
        await store.deleteDeck(a.id);
        return { ok: true, message: `Deleted ${a.label ?? "the deck"}.`, changed: true };

      case "delete_card":
        await store.deleteCard(a.id);
        return { ok: true, message: `Deleted ${a.label ?? "the card"}.`, changed: true };

      case "update_card": {
        // Build the patch from ONLY what was named. store.updateCard takes a Partial, so an
        // absent key leaves that side of the card untouched — "fix the back" must not wipe the
        // front. Report which sides actually changed, not "updated" (§2.3: the real outcome).
        const patch: CardPatch = {};
        if (a.front !== undefined) patch.front = a.front;
        if (a.back !== undefined) patch.back = a.back;
        if (a.tag !== undefined) patch.tag = a.tag;
        const fields = Object.keys(patch);
        if (!fields.length) return { ok: false, message: "Tell me what to change — the front, the back, or the tag." };
        await store.updateCard(a.id, patch);
        const named = fields.map((f) => (f === "tag" ? "tag" : `${f} side`)).join(" and ");
        return { ok: true, message: `Updated the ${named} of ${a.label ?? "that card"}.`, changed: true };
      }

      case "update_deck":
        await store.updateDeck(a.id, { name: a.name });
        return { ok: true, message: `Renamed ${a.label ?? "the deck"} to "${a.name}".`, changed: true };

      case "filter": {
        const all = await store.listCards(undefined, { limit: 50000 });
        let deckIds: Set<string> | undefined;
        if (a.filter.deckName) {
          const deck = await resolveDeck(a.filter.deckName);
          if (!deck) return { ok: false, message: `I couldn't find a deck matching "${a.filter.deckName}".` };
          deckIds = new Set([deck.id]);
        }
        const matches = applyCardFilter(all, a.filter, deckIds);
        const now = new Date();
        const rows: FilterRow[] = matches.slice(0, 50).map((c) => ({
          id: c.id, front: c.front, back: c.back, tag: c.tag, due: isDue(c, now),
        }));
        const forLabel = a.label ? ` for "${a.label}"` : "";
        return {
          ok: true,
          message: `${matches.length} card${matches.length !== 1 ? "s" : ""}${forLabel}` + (matches.length > 50 ? " (showing first 50)" : ""),
          rows,
        };
      }

      case "due_summary": {
        const decks = await store.listDecks();
        if (decks.length === 0) return { ok: true, message: 'No decks yet — say "add deck Biology" to start one.' };
        const all = await store.listCards(undefined, { limit: 50000 });
        const now = new Date();
        const lines: string[] = [];
        let total = 0;
        for (const d of decks) {
          const due = all.filter((c) => c.deck_id === d.id && isDue(c, now)).length;
          total += due;
          if (due > 0) lines.push(`• ${d.name}: ${due} due`);
        }
        return {
          ok: true,
          message: total === 0
            ? "Nothing is due right now — you're all caught up."
            : `${total} card${total !== 1 ? "s" : ""} due now:\n${lines.join("\n")}`,
        };
      }

      case "generate": {
        // The ONE billed step — reuses the app's EXISTING live endpoint. generateCards enforces
        // the study-tool fence pre-call and bills the Oriora wallet; errors (BrainError) are
        // caught below and shown in the chat. Cards land in the named (or newly created) deck.
        const cards = await generateCards(a.notes);
        if (cards.length === 0) return { ok: false, message: "I couldn't get study cards out of that — try pasting fuller notes." };
        let deck = a.deckName ? await resolveDeck(a.deckName) : null;
        if (!deck && a.deckName) deck = await store.createDeck({ name: a.deckName });
        if (!deck) deck = (await resolveDeck(undefined)) ?? (await store.createDeck({ name: `From notes ${new Date().toISOString().slice(0, 10)}` }));
        const n = await store.createCards(deck.id, cards.map((c) => ({ ...c, source: "notes" as const })));
        return { ok: true, message: `Made ${n} card${n !== 1 ? "s" : ""} from your notes → deck "${deck.name}". Routed by Oriora.`, changed: true };
      }

      case "backup_save": {
        let provider: string;
        try {
          ({ provider } = await cloudBackupSave(JSON.parse(await exportBackup())));
        } catch (e) {
          // A backup past the standard path's size limit → the device-direct GitHub
          // save (any size, straight from this device, never through Oriora). Chat can DO this
          // one itself when GitHub is already connected in the backup menu; it can't run the
          // one-time OAuth login, so when not connected it points there by name instead.
          if (e instanceof BrainError && e.code === "too_large") {
            const connected = await invoke<boolean>("has_github_restore_token").catch(() => false);
            if (connected) {
              await invoke("backup_to_github_token");
              return { ok: true, message: "Your backup was too large for the standard path, so I saved it straight from this device to your GitHub — any size works that way, and it never passes through Oriora." };
            }
            return { ok: false, message: 'This backup is too large for the standard path. One-time fix: open the backup menu (☁ icon, top right) → "Log in with GitHub" — after that, large backups save directly from this device, and I can run them for you from chat.' };
          }
          throw e;
        }
        const name = provider === "github" ? "GitHub" : provider === "gdrive" ? "Google Drive" : "cloud";
        return { ok: true, message: "Backed up your whole collection to your " + name + " — restore it on any device, any time." };
      }

      case "backup_status": {
        // The RESTORE STORY in words — same ground truth the panel's status line shows.
        const s = await cloudBackupStatus();
        if (s.connected.length === 0) {
          const deviceGh = await invoke<boolean>("has_github_restore_token").catch(() => false);
          if (deviceGh) {
            return { ok: true, message: "GitHub is logged in on this device (backup menu), so backups save and restore directly from here — any size, never passing through Oriora. Optionally, connecting a cloud at orioralabs.com → Settings → Connections adds the everyday path all your Oriora apps share." };
          }
          return { ok: true, message: 'Nothing is connected yet. Two options: connect GitHub or Google Drive once at orioralabs.com → Settings → Connections (all your Oriora apps back up there), or open the backup menu here (☁ icon) → "Log in with GitHub" — that one stays on this device only, and handles any size.' };
        }
        const name = (p: string) => (p === "gdrive" ? "Google Drive" : "GitHub");
        const when = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : null);
        const lines: string[] = [];
        const w = s.writes_to;
        if (w) {
          const slot = s.slots[w];
          lines.push(`Your backups write to ${name(w)} (connected in your Oriora account)` +
            (slot?.backup_exists ? ` — last backup ${when(slot.updated_at)}.` : " — no backup saved yet."));
        }
        for (const p of s.connected.filter((c) => c !== w)) {
          const slot = s.slots[p];
          lines.push(`${name(p)} is also connected but not used for new backups (${name(w!)} takes priority)` +
            (slot?.backup_exists ? ` — it still holds an older backup from ${when(slot.updated_at)}.` : "."));
        }
        const deviceGh = await invoke<boolean>("has_github_restore_token").catch(() => false);
        if (deviceGh) {
          lines.push("GitHub is also logged in on this device, so large backups save and restore directly from here — any size, never through Oriora.");
        }
        lines.push('Say "restore my decks" and I\'ll read the backup back in — or name a source, like "restore from Drive".');
        return { ok: true, message: lines.join("\n") };
      }

      case "backup_restore": {
        const src = a.provider ? (a.provider === "gdrive" ? "Google Drive" : "GitHub") : "cloud";
        let st;
        try {
          st = await cloudBackupLoad(a.provider);
        } catch (e) {
          // Large-backup handoff: if GitHub is connected on this device, chat pulls it directly
          // itself (the user already confirmed the REPLACE before this executes); it can't run the
          // one-time OAuth login, so when not connected it points at the button by name instead.
          if (e instanceof BrainError && e.code === "unavailable") {
            const connected = await invoke<boolean>("has_github_restore_token").catch(() => false);
            if (connected && (!a.provider || a.provider === "github")) {
              await invoke("restore_from_github_token");
              return { ok: true, message: "Your backup was too large for the standard path, so I pulled it straight from your GitHub onto this device — your data is back.", changed: true };
            }
            return { ok: false, message: `Reading your ${src} backup through Oriora didn't work — if your backup is large, use the backup menu (☁ icon, top right) → "Log in with GitHub", which backs up and restores any size directly on this device.` };
          }
          throw e;
        }
        if (!st.connected) return { ok: false, message: "Connect GitHub or Google Drive in your Oriora account first (Settings → Service connections)." };
        if (!st.data) return { ok: false, message: a.provider ? `No backup found in your ${src} yet.` : 'No cloud backup found yet — say "back up" first.' };
        await importBackup(JSON.stringify(st.data));
        return { ok: true, message: `Restored from your ${st.provider === "gdrive" ? "Google Drive" : "GitHub"} backup.`, changed: true };
      }

      case "save_local_copy": {
        const saved = await saveCopyNow(LOCAL_SLUG);
        return saved
          ? { ok: true, message: "Saved a copy of your data to the folder you picked — it's yours to keep or move." }
          : { ok: true, message: "Cancelled — no copy saved." };
      }

      case "import_file": {
        const json = await importFromFile();
        if (json === null) return { ok: true, message: "No file chosen — nothing imported." };
        await importBackup(json);
        return { ok: true, message: "Imported your data from the file — this device now matches that backup.", changed: true };
      }

      case "save_to_cloud": {
        // Save a single note or the whole conversation to the customer's OWN cloud (their GitHub/Drive),
        // distinct from backup_save (the whole decks+cards collection). Oriora retains nothing.
        const content = a.contentKind === "conversation" ? (ctx?.transcript ?? "") : (a.note ?? "");
        if (!content.trim()) {
          return { ok: false, message: a.contentKind === "conversation" ? "There's nothing to save yet — chat with me a bit first, then ask again." : "Tell me what to save and I'll store it." };
        }
        const { provider } = await cloudStoreSave(content, { name: a.contentKind, kind: a.contentKind, destination: a.destination });
        const where = provider === "github" ? "GitHub" : provider === "gdrive" ? "Google Drive" : "your cloud";
        const what = a.contentKind === "conversation" ? "our conversation" : "your note";
        return { ok: true, message: `Saved ${what} to your ${where} — it's yours to keep.` };
      }

      case "remember": {
        addInstruction(a.instruction);
        return { ok: true, message: "Noted — from now on: " + a.instruction };
      }

      // ── ARTIFACT TOOLS (the library) — device-only, fleet-wide ──────────────
      case "library_save": {
        const _item = libraryAdd(a.title, a.itemKind, a.content, a.folder ?? "");
        return { ok: true, message: `Saved "${_item.title}" to your library${_item.folder ? ` (folder: ${_item.folder})` : ""} — on this device, yours.`, changed: true };
      }
      case "library_list": {
        const _items = libraryList(a.folder);
        if (!_items.length) return { ok: true, message: a.folder ? `Nothing in "${a.folder}" yet.` : "Your library is empty — ask me to write you something and I'll keep it here." };
        return { ok: true, message: (a.folder ? `In "${a.folder}":\n` : "Your library:\n") + _items.map((i) => `• ${i.title} — ${i.kind}${i.folder ? ` · ${i.folder}` : ""}`).join("\n") };
      }
      case "library_remove":
        return libraryRemove(a.id)
          ? { ok: true, message: `Removed ${a.label ?? "it"} from your library.`, changed: true }
          : { ok: false, message: "That item isn't in your library any more." };
      case "library_move":
        return libraryMove(a.id, a.folder)
          ? { ok: true, message: `Moved ${a.label ?? "it"} to "${a.folder}".`, changed: true }
          : { ok: false, message: "That item isn't in your library any more." };
      case "library_merge": {
        const _merged = libraryMerge(a.ids, a.title);
        return _merged
          ? { ok: true, message: `Merged into "${_merged.title}" — the originals are folded in.`, changed: true }
          : { ok: false, message: "I need at least two existing items to merge." };
      }


      default:
        return { ok: false, message: "I didn't catch that — try again?" };
    }
  } catch (e) {
    return { ok: false, message: `That didn't work: ${e instanceof Error ? e.message : String(e)}` };
  }
}

/** Tiny helper the router uses for delete-target resolution messages. */
export function cardLabel(c: Card): string {
  const f = c.front.length > 60 ? `${c.front.slice(0, 57)}…` : c.front;
  return `"${f}"`;
}

export { normalizeTag };
